import { useCallback, useEffect, useState } from "react";
import { ResetFiltersButton } from "@/components/common/ResetFiltersButton";
import { useSearchParams } from "react-router-dom";
import { Globe, Rocket, ListTree, Boxes, ChevronRight, Lightbulb } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProductSummary } from "@/components/product/ProductSummary";
import { InnovationsSummaryCard } from "@/components/product/InnovationsSummaryCard";
import { InnovationsPanel } from "@/components/product/InnovationsPanel";
import { ReviewBadge } from "@/components/product/ReviewBadge";
import { ModulesPanel } from "@/components/product/ModulesPanel";
import { PlanetsPanel } from "@/components/product/PlanetsPanel";
import { useInnovation } from "@/context/InnovationContext";
import { moduleStatusFromSlug } from "@/data/productOptions";
import { innStatusFromSlug } from "@/data/innovationOptions";
import { api } from "@/lib/api";

const HIERARCHY = [
  { label: "Planet", icon: Globe, color: "#0EA5A4" },
  { label: "Module", icon: Rocket, color: "#1D4ED8" },
  { label: "Chapter", icon: ListTree, color: "#F97316" },
  { label: "Content Item", icon: Boxes, color: "#7C3AED" },
];

export default function ProductContent() {
  const [bump, setBump] = useState(0);
  const [planets, setPlanets] = useState([]);
  const [modules, setModules] = useState([]);
  const [tab, setTab] = useState("planets");
  const [innoView, setInnoView] = useState("all");
  const [innoNav, setInnoNav] = useState(0);
  const [innoStatus, setInnoStatus] = useState(null);
  const [modStatus, setModStatus] = useState(null);
  const [modOverdue, setModOverdue] = useState(false);
  const [modNav, setModNav] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const { refresh: refreshInno } = useInnovation();

  const load = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([api.list("planets"), api.list("modules")]);
      setPlanets(p); setModules(m);
    } catch (err) { console.error("Failed to load product data:", err); }
  }, []);
  useEffect(() => { load(); }, [load, bump]);

  // Deep-link handler: reads ?tab= / ?status= / ?view= (and legacy ?innovations=review).
  // Invalid params fall back safely to the current/default view.
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const legacyInno = searchParams.get("innovations");
    let target = null;
    if (["planets", "modules", "innovations"].includes(tabParam)) target = tabParam;
    else if (legacyInno === "review") target = "innovations";
    if (!target) return;
    setTab(target);
    if (target === "modules") {
      setModStatus(moduleStatusFromSlug(searchParams.get("status")));
      setModOverdue(searchParams.get("filter") === "overdue");
      setModNav((n) => n + 1);
    } else if (target === "innovations") {
      setInnoView(searchParams.get("view") === "review" || legacyInno === "review" ? "reviewq" : "all");
      setInnoStatus(innStatusFromSlug(searchParams.get("status")));
      setInnoNav((n) => n + 1);
      if (legacyInno) { const p = new URLSearchParams(searchParams); p.delete("innovations"); setSearchParams(p, { replace: true }); }
    }
  }, [searchParams, setSearchParams]);

  const changeTab = (v) => setSearchParams({ tab: v });
  const clearParam = (key) => { const p = new URLSearchParams(searchParams); p.delete(key); setSearchParams(p); };
  const clearModuleFilters = () => { const p = new URLSearchParams(searchParams); p.delete("status"); p.delete("filter"); setSearchParams(p); };

  const onChange = () => { setBump((b) => b + 1); refreshInno(); };

  return (
    <>
      <PageHeader
        eyebrow="The Edjuverse"
        title="Product & Content"
        description="Oversee Edju Cat learning content across the exploration hierarchy."
      />

      {/* Hierarchy legend */}
      <div data-testid="product-hierarchy" className="mb-8 flex flex-wrap items-center gap-2">
        {HIERARCHY.map((h, i) => {
          const Icon = h.icon;
          return (
            <div key={h.label} className="flex items-center gap-2">
              <span
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold"
                style={{ color: h.color, borderColor: `${h.color}55`, backgroundColor: `${h.color}12` }}
              >
                <Icon size={14} strokeWidth={2.4} /> {h.label}
              </span>
              {i < HIERARCHY.length - 1 && <ChevronRight size={15} className="text-[#9DAAC6]" />}
            </div>
          );
        })}
      </div>

      <div className="mb-8"><ProductSummary refreshKey={bump} /></div>

      <InnovationsSummaryCard refreshKey={bump} />

      <ResetFiltersButton /><Tabs value={tab} onValueChange={changeTab} className="w-full">
        <TabsList data-testid="product-tabs" className="mb-6 flex h-auto flex-wrap justify-start gap-1.5 rounded-full border border-[#E4E9F3] bg-white p-1.5 shadow-sm">
          <TabsTrigger value="planets" data-testid="tab-planets" className="rounded-full px-4 py-1.5 text-sm font-semibold text-[#5A6B8C] transition-colors data-[state=active]:text-white data-[state=active]:[background-color:var(--accent-primary)]">Planets</TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-modules" className="rounded-full px-4 py-1.5 text-sm font-semibold text-[#5A6B8C] transition-colors data-[state=active]:text-white data-[state=active]:[background-color:var(--accent-primary)]">Modules</TabsTrigger>
          <TabsTrigger value="innovations" data-testid="tab-innovations" className="rounded-full px-4 py-1.5 text-sm font-semibold text-[#5A6B8C] transition-colors data-[state=active]:text-white data-[state=active]:[background-color:#7C3AED]"><Lightbulb size={14} className="mr-1.5" /> Future Innovations <ReviewBadge static className="ml-2" /></TabsTrigger>
        </TabsList>
        <TabsContent value="planets"><PlanetsPanel modules={modules} onChange={onChange} /></TabsContent>
        <TabsContent value="modules"><ModulesPanel planets={planets} onChange={onChange} initialStatus={modStatus} initialOverdue={modOverdue} navKey={modNav} onClearStatus={clearModuleFilters} /></TabsContent>
        <TabsContent value="innovations"><InnovationsPanel onChange={onChange} initialView={innoView} initialStatus={innoStatus} navKey={innoNav} onClearStatus={() => clearParam("status")} /></TabsContent>
      </Tabs>
    </>
  );
}
